import { MEASUREMENT_TYPES } from "../../../../../config/enums/MeasurementTypes";
import { toLabelMap } from "../../shared/utils/staticDataOptions.utils";
import {
  ApiFacility,
  ApiProcessingRecord,
  FacilityOption,
  ProcessEditFormValues,
  ProcessRecordLookups,
  ProcessSaveContext,
  ProcessTypeOption,
  SelectOption
} from "../types/processRecords.types";

/** `facilityId` is a bare string in farmGate storage, a populated object in processGate storage. */
export const resolveFacilityId = (facilityId: string | ApiFacility | null | undefined): string => {
  if (typeof facilityId === "object" && facilityId !== null) return facilityId._id ?? "";
  return facilityId ?? "";
};

/** Display name for a record's facility. Falls back to an em dash. */
export const resolveFacilityName = (
  facilityId: string | ApiFacility | null | undefined,
  facilityNameMap: ProcessRecordLookups["facilityNameMap"]
): string => {
  if (typeof facilityId === "object" && facilityId !== null) {
    return facilityId.facilityName ?? "—";
  }
  return facilityNameMap[facilityId as string] ?? "—";
};

/**
 * Display name for a process type. Falls back to a plain hyphen — deliberately
 * different from {@link resolveFacilityName}'s em dash, matching existing output.
 */
export const resolveProcessTypeName = (
  processTypeNameMap: ProcessRecordLookups["processTypeNameMap"],
  value: string | number | null | undefined
): string => processTypeNameMap[String(value ?? "")] || "-";

const numberToFormValue = (value: number | null | undefined): string =>
  value == null ? "" : String(value);

const formValueToNumber = (value: string): number => (value === "" ? 0 : Number(value));

/** Seeds the inline editor's `useForm` from an API record. */
export const mapRecordToFormValues = (record: ApiProcessingRecord): ProcessEditFormValues => ({
  processingName: record.processingName ?? "",
  processingType: String(record.processingType ?? ""),
  facilityName: resolveFacilityId(record.facilityId),
  capacity: numberToFormValue(record.capacity),
  capacityUnit: String(record.capacityUnit ?? MEASUREMENT_TYPES.WEIGHT_KG),
  input: numberToFormValue(record.input),
  inputUnit: String(record.inputUnit ?? MEASUREMENT_TYPES.WEIGHT_KG),
  output: numberToFormValue(record.output),
  outputUnit: String(record.outputUnit ?? MEASUREMENT_TYPES.WEIGHT_KG),
  wasteSideStream: numberToFormValue(record.sideStream),
  wasteSideStreamUnit: String(record.sideStreamUnit ?? MEASUREMENT_TYPES.WEIGHT_KG),
  electricity: numberToFormValue(record.electricity),
  water: numberToFormValue(record.water),
  heat: numberToFormValue(record.heat)
});

/** Folds submitted form values back onto the record the editor was seeded from. */
export const mapFormValuesToRecord = (
  values: ProcessEditFormValues,
  record: ApiProcessingRecord
): ApiProcessingRecord =>
  ({
    ...record,
    processingName: values.processingName,
    processingType: Number(values.processingType),
    facilityId: values.facilityName,
    capacity: formValueToNumber(values.capacity),
    capacityUnit: Number(values.capacityUnit),
    input: formValueToNumber(values.input),
    inputUnit: Number(values.inputUnit),
    output: formValueToNumber(values.output),
    outputUnit: Number(values.outputUnit),
    sideStream: formValueToNumber(values.wasteSideStream),
    sideStreamUnit: Number(values.wasteSideStreamUnit),
    electricity: formValueToNumber(values.electricity),
    water: formValueToNumber(values.water),
    heat: formValueToNumber(values.heat)
  } as ApiProcessingRecord);

/**
 * Builds the `PUT /{gate}/processing/{processId}` body.
 *
 * `chemical` and `wasteManagements` are ALWAYS stripped: they have their own CRUD
 * endpoints (see processDetail/hooks) and must never round-trip through the process
 * PUT — the bulk row the inline editor operates on holds bare ids (farmGate) or is
 * simply stale, so sending it back would corrupt those sub-records.
 *
 * `cipSip` rides this PUT, so it is kept only when the caller has an authoritative
 * copy (`includeCipSip: true`, sourced from the loaded ProcessDetail). The inline
 * row editor has no loaded detail, so it omits the key entirely rather than sending
 * `[]` or the bulk row's stale value — which would blank CIP/SIP on every row edit.
 */
export const buildProcessUpdateDto = (
  updated: ApiProcessingRecord,
  ctx: ProcessSaveContext,
  { includeCipSip }: { includeCipSip: boolean }
): Record<string, unknown> => {
  // DTOs omit _id — it goes in the URL as processId only.
  const { _id, chemical, wasteManagements, cipSip, ...rest } = updated;

  const base: Record<string, unknown> = {
    ...rest,
    processId: _id,
    facilityId: resolveFacilityId(updated.facilityId)
  };

  if (includeCipSip) base.cipSip = cipSip;

  // FarmGateProcessing has no processGateId/ownerId/sourceId fields.
  if (ctx.gateKind === "farm") return base;

  return {
    ...base,
    processGateId: ctx.processGateId,
    ownerId: updated.ownerId ?? ctx.ownerId,
    sourceId: updated.sourceId ?? ctx.sourceId
  };
};

export const buildProcessTypeOptions = (
  processTypesWholeList: ProcessTypeOption[] | undefined
): SelectOption[] =>
  (processTypesWholeList ?? []).map(t => ({ value: String(t.value), label: t.text }));

export const buildFacilityOptions = (
  facilityWholeList: FacilityOption[] | undefined
): SelectOption[] => (facilityWholeList ?? []).map(f => ({ value: f.value, label: f.text }));

/** id → display-name map built from a static-data option list. */
export const buildNameMap = toLabelMap;
